import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { LoadingIndicator } from "components/framework/loadingIndicator/LoadingIndicator";
import { IntlShape, useIntl } from "react-intl";
import { getValueOrEmpty, truncateAndAppendEllipsis } from "services/util/StringUtil";
import ReactTable from "components/framework/table/ReactTable";
import { TradingPartnerActionCell } from "components/common/actions/TradingPartnerActionCell";
import { authorize } from "services/authorization/AuthorizationService";
import { PermissionType } from "services/authorization/PermissionType";
import { handleError } from "services/util/ApiUtil";
import { AppContext } from "services/appContext/AppContext";
import { useIsMounted } from "services/customHooks/useIsMounted";
import { TableIdentifiers } from "services/uiSettings/TableIdentifiers";
import { TradingPartnerAdminTableColumnIdentifiers } from "components/networkExplorer/types/TradingPartnerColumnIdentifier";
import { WebhookLogsURLTableColumnIdentifiers } from "./types/WebhookLogsIdentifier";
import { WebhookLogsApi } from "services/apis/WebhookLogsApi";
import { WebhookUrlLogs } from "services/apis/types/webhookLogs/WebhookUrlLogsDto";
import FormatDateTime from "components/framework/date/FormatDateTime";
import { DefaultWebhookLogsListFilter, WebhookLogsListFilter } from "services/apis/types/webhookLogs/WebhookLogsListFilter";
import { WebhookLogsStatus, buildQueryParams } from "services/util/WebhookLogsUtil";
import moment from "moment";
import UrlNotificationActionsCell from "./UrlNotificationActionsCell";

type ColumnFilterSort = {
  isSorted?: boolean;
  isSortedDesc?: boolean;
}
type Props = {
  search: string;
};

export default function UrlNotificationsTable(props: Props) {
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [webhookUrlLogsDetails, setWebhookUrlLogsDetails] = useState<WebhookUrlLogs[]>();
  const [userIsAdmin] = useState(authorize(PermissionType.Admin));
  const { appContext } = useContext(AppContext);
  const isMounted = useIsMounted();
  const intl = useIntl();
  const [searchKey, setSearchKey] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [count, setCount] = useState(0);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<WebhookLogsListFilter>(DefaultWebhookLogsListFilter);
  const [isAppStartUp, setIsAppStartUp] = useState(false);
  const [orderByColumn, setOrderByColumn] = useState("");
  const [orderDirection, setOrderDirection] = useState("");
  const [columnFilterSort, setColumnFilterSort] = useState<{ [key: string]: ColumnFilterSort }>({});
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [orderId, setOrderId] = useState("");

  const queryString = new URLSearchParams(
    Object.fromEntries(
      Object.entries(selectedFilter).filter(([_, v]) => v !== "" && v !== "Any")
    )
  ).toString();

  const resetAllFilters = useCallback(() => {
    if (isMounted.current) {
      setSelectedFilter(DefaultWebhookLogsListFilter);
    }
  }, [isMounted]);

  const toggleRefresh = useCallback(() => {
    if (isMounted.current) {
      setTriggerRefresh((x) => !x);
    }
  }, [isMounted]);

  const areFiltersmodified = useCallback((): boolean => {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(selectedFilter).filter(([_, v]) => v !== "" && v !== "Any"))
    ).toString();
    return queryString ? true : false;
  }, [selectedFilter]);

  const updateHiddenColumns = useCallback((columns) => {
    const excludedColumnsForWholesale = [TradingPartnerAdminTableColumnIdentifiers.RequestingSpId];
    if (!userIsAdmin) {
      return columns.filter(column => column.identifier && !excludedColumnsForWholesale.includes(column.identifier))
    };
    return columns;
  }, [userIsAdmin]);

  const columnFilter = useCallback(
    (rows: any[], accessor: any, filterValues: any) => {
      if (selectedFilter[accessor[0]] !== filterValues) {
        setSelectedFilter((prevState) => ({
          ...prevState,
          ...{
            [accessor[0]]: filterValues
          }
        }));
      }
      return rows;
    },
    [selectedFilter]
  );

  const handleSearch = (data) => {
    const formattedData = {
      orderId: data.orderIdValue ? data.orderIdValue : '',
      Status: data.statusValue ? data.statusValue : '',
      FromDate: data.fromDate ? data.fromDate : '',
      ToDate: data.toDate ? data.toDate : ''
    };
    setOrderId(formattedData.orderId);
    setStatus(formattedData.Status);
    setFromDate(formattedData.FromDate);
    setToDate(formattedData.ToDate);
  };

  const getWebhookLogsList = (
    currentPage: number,
    pageSize: number,
    orderByColumn: string,
    orderDirection: string,
    filterValues: {
        orderId?: string,
        Status?: string,
        FromDate?: string,
        ToDate?: string
    },
    tenant: string | undefined = undefined
) => {
    const params = buildQueryParams({
        pageNumber: currentPage.toString(),
        pageSize: pageSize.toString(),
        orderByColumn: orderByColumn,
        orderDirection: orderDirection,
        orderId: filterValues.orderId,
        Status: filterValues.Status,
        FromDate: filterValues.FromDate,
        ToDate: filterValues.ToDate
    });

    WebhookLogsApi.getWebhookUrlLogsList(params, tenant)
        .then((result) => {
            if (isMounted && result) {
                if (result.rows) {
                    setWebhookUrlLogsDetails(result.rows);
                    setCount(result.totalRows);
                }
            }
        })
        .catch((error) => handleError(error))
        .finally(() => {
            if (isMounted) {
                setShowLoadingIndicator(false);
            }
        });
};

  useEffect(() => {
    if (isMounted.current && isAppStartUp) {
      setWebhookUrlLogsDetails(undefined);
      const queryString = new URLSearchParams(
        Object.fromEntries(
          Object.entries(selectedFilter).filter(([_, v]) => v !== "" && v !== "Any")
        )
      ).toString();
      getWebhookLogsList(pageIndex + 1, pageSize, orderByColumn, orderDirection, {
        orderId: orderId,
        Status: status,
        FromDate: fromDate,
        ToDate: toDate
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, pageIndex, pageSize, status, fromDate, toDate, orderId ]);

  useEffect(() => {
    if (isMounted.current && isAppStartUp) {
      setWebhookUrlLogsDetails(undefined);
      if (pageIndex !== 0) setPageIndex(0);
      else {
        const queryString = new URLSearchParams(
          Object.fromEntries(
            Object.entries(selectedFilter).filter(([_, v]) => v !== "" && v !== "Any")
          )
        ).toString();
        getWebhookLogsList(pageIndex + 1, pageSize, orderByColumn, orderDirection, {
          orderId: orderId,
          Status: status,
          FromDate: fromDate,
          ToDate: toDate
      });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, selectedFilter]);

  const columns = useMemo(() => {
    const columnsList = getColumns(intl, columnFilter, toggleRefresh);
    return updateHiddenColumns(columnsList);
  }, [intl, updateHiddenColumns, columnFilter, toggleRefresh]);

  const handlePageChange = (pageIndex) => {
    setPageIndex(pageIndex);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event);
    setPageIndex(0);
  };

  useEffect(() => {
    if (triggerRefresh) {
      setSelectedFilter(DefaultWebhookLogsListFilter);
      if (pageIndex !== 0) setPageIndex(0);
      setShowLoadingIndicator(true);
      setTriggerRefresh(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, triggerRefresh]);

  useEffect(() => {
    if (isMounted.current) {
      // getTppSpidList();
      setShowLoadingIndicator(true);
      setIsAppStartUp(true);
      setSearchKey("");
      if (pageIndex !== 0 || pageSize !== 10 || areFiltersmodified()) {
        if (pageIndex !== 0) setPageIndex(0);
        if (pageSize !== 10) setPageSize(10);
        if (areFiltersmodified()) resetAllFilters();
      } else getWebhookLogsList(1, 10, orderByColumn, orderDirection, {
        orderId: orderId,
        Status: status,
        FromDate: fromDate,
        ToDate: toDate
    });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, appContext.localStorageInfo.selectedProfile, triggerRefresh]);

  useEffect(() => {
    if (isMounted.current && props.search !== searchKey) {
      resetAllFilters();
      setSearchKey(props.search);
      setShowLoadingIndicator(true);
      setIsAppStartUp(true);
      if (pageIndex !== 0 || pageSize !== 10 || areFiltersmodified()) {
        if (pageIndex !== 0) setPageIndex(0);
        if (pageSize !== 10) setPageSize(10);
        if (areFiltersmodified()) resetAllFilters();
      } else getWebhookLogsList(1, 10, orderByColumn, orderDirection, {
        orderId: orderId,
        Status: status,
        FromDate: fromDate,
        ToDate: toDate
    });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, props.search, searchKey, resetAllFilters]);

  const sortableColumns = [
    WebhookLogsURLTableColumnIdentifiers.OrderId,
  ];

  //Handle Event for sorting
  const handleColumnSorted = useCallback(
    (column) => {
      const columnSortOption = columnFilterSort[column.identifier];
      if (sortableColumns.includes(column.identifier)) return;
      if (!columnSortOption || !columnSortOption.isSorted) {
        // Column is not sorted or in an unsorted state, set it to ascending
        setColumnFilterSort((prevSortOptions) => ({
          ...prevSortOptions,
          [column.identifier]: {
            isSorted: true,
            isSortedDesc: false,
          },
        }));
        setOrderDirection("asc");
        setOrderByColumn(column.identifier);
        getWebhookLogsList(pageIndex + 1, pageSize, column.identifier, "asc", {
          orderId: orderId,
          Status: status,
          FromDate: fromDate,
          ToDate: toDate
        }
        ); // Use pageSize
      } else if (columnSortOption.isSorted && !columnSortOption.isSortedDesc) {
        // Column is sorted in ascending order, toggle to descending
        setColumnFilterSort((prevSortOptions) => ({
          ...prevSortOptions,
          [column.identifier]: {
            isSorted: true,
            isSortedDesc: true,
          },
        }));
        setOrderDirection("desc"); // Set order direction
        setOrderByColumn(column.identifier); // Set orderByColumn
        getWebhookLogsList(pageIndex + 1, pageSize, column.identifier, "desc", {
          orderId: orderId,
          Status: status,
          FromDate: fromDate,
          ToDate: toDate
      }); // Use pageSize
      } else {
        // Column is sorted in descending order, toggle to unsorted
        setColumnFilterSort((prevSortOptions) => ({
          ...prevSortOptions,
          [column.identifier]: {
            isSorted: false,
            isSortedDesc: false,
          },
        }));
        setOrderDirection(""); // Clear order direction
        setOrderByColumn("column.identifier"); // Clear orderByColumn
        getWebhookLogsList(pageIndex + 1, pageSize, column.identifier, "", {
          orderId: orderId,
          Status: status,
          FromDate: fromDate,
          ToDate: toDate
      });
      }
    },
    [columnFilterSort, getWebhookLogsList, pageIndex, pageSize, status, fromDate, toDate]
  );

  return showLoadingIndicator ? (
    <LoadingIndicator white />
  ) : (
    <ReactTable
      titleComponent={getTitleComponent(intl.formatMessage({ id: "webhookLogs.url.table.title" }))}
      identifier={TableIdentifiers.WebhookLogs}
      data={webhookUrlLogsDetails}
      columns={columns}
      showExport={true}
      useColumnFilters
      onRefresh={toggleRefresh}
      hideSearch={true}
      queryCount={count}
      queryPageSize={pageSize}
      queryPageIndex={pageIndex}
      handlePageChange={handlePageChange}
      handlePageSizeChange={handlePageSizeChange}
      handleResetFilterChange={resetAllFilters}
      autoResetFilters={false}
      handleColumnSorted={handleColumnSorted}
      columnSortOptions={columnFilterSort}
      options={WebhookLogsStatus}
      handleSearch={handleSearch}
      hasCustomFilters={true}
      showCopy={false}
      showPrint={false}
    />
  );
}

const getTitleComponent = (title) => {
  return (
    <div className="flex-fill">
      <h3 className="mb-2">{title}</h3>
    </div>
  );
};

const getColumns = (
  intl: IntlShape,
  customfilter: (x: any, y: any, z: any) => void,
  toggleRefresh
) => [
    {
      Header: intl.formatMessage({ id: "webhookLogs.table.orderId" }),
      accessor: "orderId",
      identifier: WebhookLogsURLTableColumnIdentifiers.OrderId,
      Cell: (cell: any) => getValueOrEmpty(cell.cell.row.original.orderId)
    },
    {
      Header: intl.formatMessage({ id: "webhookLogs.table.url" }),
      accessor: "endpoint",
      identifier: WebhookLogsURLTableColumnIdentifiers.Url,
      Cell: (cell: any) => (
        <span title={getValueOrEmpty(cell.cell.row.original.endpoint)}>
          {truncateAndAppendEllipsis(cell.cell.row.original.endpoint)}
        </span>
      )
    },
    {
      Header: intl.formatMessage({ id: "webhookLogs.table.status" }),
      accessor: "status",
      identifier: WebhookLogsURLTableColumnIdentifiers.Status,
      Cell: (cell: any) => getValueOrEmpty(cell.cell.row.original.status)
    },
    {
      Header: intl.formatMessage({ id: "webhookLogs.table.creationDate" }),
      accessor: "creationDate",
      identifier: WebhookLogsURLTableColumnIdentifiers.CreationDate,
      className: "max-width-200 padding-horizontal-8",
      Cell: (cell: any) => (cell.cell.value ? <FormatDateTime utcDate={cell.cell.value} /> : "")
    },
    {
      Header: intl.formatMessage({ id: "webhookLogs.table.actions" }),
      identifier: WebhookLogsURLTableColumnIdentifiers.Actions,
      Cell: (cell: any) => (
        <UrlNotificationActionsCell
          status={cell.cell.row.original.status}
          retryCount={cell.cell.row.original.retry_count}
          id={cell.cell.row.original.id}
          onActionExecuted={toggleRefresh}
        />
      )
    }
  ];
